/* Margin Finder · Seguros: o que o `agent_seguros` achou, um usuário por vez.
 *
 * Único agente cujos totais já chegam ANUAIS — apólice se contrata por ano, e o
 * herói fala por ano para não inventar um mensal que o contrato não tem.
 *
 * A tela responde duas perguntas em sequência, e a ordem importa: primeiro "o que
 * você paga hoje" (as cobranças achadas no extrato, muitas com ramo indeterminado),
 * depois "o que uma carteira que cobre os ramos do crivo custaria". A economia é a
 * diferença — e ela só vale se as cobranças de hoje forem mesmo o que parecem, por
 * isso `avaliacao` e `alerta` sobem para chip em cada linha.
 */

import { ChartFrame, Card } from "../../components/ui";
import type { CarteiraItem, SeguroAtual, SegurosUser } from "../../lib/api";
import { brl2 } from "../../lib/format";
import { RelatorioEmTexto } from "./Relatorio";
import {
  Barras, BlocoAuditoria, CONFIANCA, Facts, Hero, Passos, PlanoShell, Provenancia,
  SecaoMargin, economiaLabel, lista, money, ou, rotulo,
} from "./comum";

/* ── rótulos ─────────────────────────────────────────────────────────────── */

const RAMO: Record<string, string> = {
  residencial: "residencial",
  rc_familiar: "RC familiar",
  vida: "vida",
  auto: "auto",
  prestamista: "prestamista",
  indeterminado: "ramo indeterminado",
};

const AVALIACAO: Record<string, string> = {
  verificar: "verificar",
  manter: "manter",
  substituir: "substituir",
  cancelar: "cancelar",
  identificar: "identificar",
};

const ALERTA: Record<string, string> = {
  nao_identificada: "seguradora não identificada",
  seguradora_reprovada: "seguradora reprovada",
  cobranca_parou: "cobrança parou",
  possivel_premio_anual: "possível prêmio anual",
};

const BASE_ANUAL: Record<string, string> = {
  mensal_x12: "mensal × 12",
  meses_observados: "meses observados",
  cobranca_unica: "cobrança única",
};

const ORIGEM: Record<string, string> = {
  catalogo: "do catálogo",
  manter_atual: "manter a atual",
};

const BASE_PRECO: Record<string, string> = {
  taxa_x_capital: "taxa × capital",
  custo_atual: "custo atual",
};

const MOTIVO: Record<string, string> = {
  estouraria_o_teto: "estouraria o teto",
  depende_do_residencial: "depende do residencial",
};

const BASE_TETO: Record<string, string> = {
  gasto_atual: "o gasto de hoje",
  orcamento_do_crivo: "o orçamento do crivo",
  sem_teto: "sem teto",
};

const STATUS: Record<string, string> = {
  reduzir: "dá para reduzir",
  manter: "vale manter",
  trocar: "vale trocar",
  sem_seguros: "sem seguro no extrato",
  dados_insuficientes: "dados insuficientes",
};

/* ── as cobranças de hoje ────────────────────────────────────────────────── */

function SeguroAtualCard({ s }: { s: SeguroAtual }) {
  const grave = s.avaliacao === "cancelar" || s.avaliacao === "substituir";
  return (
    <article className="mf-assin" data-status={s.avaliacao}>
      <div className="mf-assin-head">
        <div className="mf-assin-id">
          <h3>{ou(s.descritor, s.seguradora)}</h3>
          <div className="mf-assin-meta">
            {rotulo(RAMO, s.ramo)} · {ou(s.seguradora)}
            {s.cobre_ramo && <> · cobre {rotulo(RAMO, s.cobre_ramo)}</>}
          </div>
        </div>
        <div className="mf-assin-num">
          <div className="v">{money(s.custo_mensal_brl)}<small>/mês</small></div>
          <div className="d">{money(s.custo_anual_brl)} por ano</div>
        </div>
      </div>
      <div className="chips">
        <span className={grave ? "tag alta" : "tag"}>{rotulo(AVALIACAO, s.avaliacao)}</span>
        <span className="chip">{rotulo(BASE_ANUAL, s.base_anual)}</span>
        {s.alerta && <span className="tag alta">{rotulo(ALERTA, s.alerta)}</span>}
      </div>
      {s.observacao && <p className="mf-nota">{s.observacao}</p>}
    </article>
  );
}

/* ── a carteira sugerida ─────────────────────────────────────────────────── */

function CarteiraCard({ c }: { c: CarteiraItem }) {
  return (
    <PlanoShell
      papel={rotulo(RAMO, c.ramo)}
      escolhido={c.origem === "manter_atual"}
      titulo={ou(c.produto, c.seguradora)}
      sub={c.produto ? c.seguradora : undefined}
      preco={money(c.custo_anual_brl)}
      precoNota="/ano"
      porQue={c.por_que}
      atencao={c.atencao}
      chips={
        <>
          <span className="chip">{rotulo(ORIGEM, c.origem)}</span>
          <span className="chip">{rotulo(BASE_PRECO, c.base_do_preco)}</span>
          <span className={c.confianca === "baixa" ? "tag alta" : "tag media"}>
            {rotulo(CONFIANCA, c.confianca)}
          </span>
        </>
      }
    >
      {c.capital_minimo_brl !== null && (
        <div className="mf-plan-linha">
          capital mínimo {money(c.capital_minimo_brl)}
        </div>
      )}
      {lista(c.coberturas_a_pedir).length > 0 && (
        <div className="mf-plan-cobertura">
          pedir: {lista(c.coberturas_a_pedir).join(", ")}
        </div>
      )}
    </PlanoShell>
  );
}

/* ── a sessão ────────────────────────────────────────────────────────────── */

export function Seguros() {
  return (
    <SecaoMargin<SegurosUser>
      chave="seguros"
      vazio="Usuário sem análise de seguros."
      optionLabel={economiaLabel}
    >
      {(user) => <SegurosDetalhe user={user} />}
    </SecaoMargin>
  );
}

function SegurosDetalhe({ user }: { user: SegurosUser }) {
  const a = user.analise;
  const auditoria = a.auditoria ?? {} as typeof a.auditoria;
  const atuais = [...lista(a.seguros_atuais)]
    .sort((x, y) => (y.custo_anual_brl ?? 0) - (x.custo_anual_brl ?? 0));
  const carteira = [...lista(a.carteira_alvo)].sort((x, y) => x.ordem - y.ordem);
  const fora = lista(a.fora_da_carteira);

  const barras = [
    ...(a.custo_anual_atual_brl !== null
      ? [{ key: "hoje", rotulo: "hoje", valor: a.custo_anual_atual_brl, destaque: true }]
      : []),
    ...carteira
      .filter((c) => c.custo_anual_brl !== null)
      .map((c) => ({
        key: `c${c.ramo}`,
        rotulo: rotulo(RAMO, c.ramo),
        valor: c.custo_anual_brl as number,
      })),
  ];

  return (
    <div className="mf-page">
      {/* 1 · a decisão */}
      <Card>
        <Hero
          estado={user.estado_economia}
          titulo="economia estimada"
          valor={user.economia_anual_brl === null
            ? "sem base comparável"
            : `R$ ${brl2(user.economia_anual_brl)}`}
          detalhe={<>por ano{user.economia_mensal_brl !== null
            && <> · {money(user.economia_mensal_brl)}/mês</>}</>}
          faixa
          lado={a.custo_anual_carteira_brl === null ? undefined : {
            k: "carteira sugerida",
            v: `${money(a.custo_anual_carteira_brl)}/ano`,
            d: <>{money(a.custo_mensal_carteira_brl)}/mês · {carteira.length} ramo(s)</>,
          }}
        />
        <p className="mf-frase">
          {a.status !== "reduzir" && (
            <span className="tag" style={{ marginRight: 8 }}>{rotulo(STATUS, a.status)}</span>
          )}
          {a.resumo}
        </p>
      </Card>

      {/* 2 · a evidência: o que sai hoje em apólice */}
      <Card title="O que você paga hoje"
            sub="cobranças de seguro achadas no extrato, maior primeiro">
        <Facts items={[
          { k: "seguradora principal", v: ou(a.seguradora_principal_atual) },
          { k: "sai por mês", v: money(a.custo_mensal_atual_brl) },
          { k: "sai por ano", v: money(a.custo_anual_atual_brl) },
          { k: "cobranças", v: atuais.length.toString() },
        ]} />
        {atuais.length === 0
          ? <p className="mf-nota">
              Nenhuma cobrança de seguro no extrato. Isso não prova ausência de apólice:
              o prêmio pode estar em débito em conta, boleto, folha ou na empresa.
            </p>
          : <div className="mf-assins" style={{ marginTop: 14 }}>
              {atuais.map((s, i) => <SeguroAtualCard key={i} s={s} />)}
            </div>}
      </Card>

      {/* 3 · a ação: a carteira sugerida */}
      {carteira.length > 0 && (
        <ChartFrame
          title="A carteira sugerida"
          sub={`na ordem de prioridade do crivo${
            auditoria.base_do_teto ? ` · teto por ${rotulo(BASE_TETO, auditoria.base_do_teto)}` : ""}`}
          tableHeader={["ramo", "produto", "seguradora", "R$/ano", "capital mínimo", "base"]}
          tableRows={carteira.map((c) => [
            rotulo(RAMO, c.ramo), c.produto ?? "—", c.seguradora,
            c.custo_anual_brl ?? "—", c.capital_minimo_brl ?? "—",
            rotulo(BASE_PRECO, c.base_do_preco),
          ])}
        >
          <div className="mf-plans">
            {carteira.map((c, i) => <CarteiraCard key={i} c={c} />)}
          </div>
        </ChartFrame>
      )}

      {/* 4 · o que ficou de fora, e por quê */}
      {fora.length > 0 && (
        <Card title="Fora da carteira"
              sub="ramos do crivo que não couberam, e o que isso custaria">
          <div className="mf-achados">
            {fora.map((f, i) => (
              <article className="mf-achado" key={i}>
                <div className="mf-plan-papel">{rotulo(MOTIVO, f.motivo)}</div>
                <h3>{rotulo(RAMO, f.ramo)}</h3>
                <div className="mf-achado-impacto">
                  {money(f.custo_anual_estimado_brl)} por ano
                </div>
                {f.atencao && <p className="mf-atencao">{f.atencao}</p>}
              </article>
            ))}
          </div>
        </Card>
      )}

      {/* 5 e 6 · a ação */}
      <Passos title="Como obter" sub="o caminho até a apólice"
              items={lista(a.como_obter)} />
      <Passos title="Antes de contratar" items={lista(a.antes_de_contratar)} />

      {/* 7 · o método */}
      {barras.length > 1 && (
        <ChartFrame
          title="O custo de cada ramo"
          sub="custo anual por linha da carteira, contra o que sai hoje"
          tableHeader={["linha", "R$/ano"]}
          tableRows={barras.map((b) => [b.rotulo, b.valor])}
        >
          <Barras rows={barras} />
        </ChartFrame>
      )}

      {/* 8 · as ressalvas */}
      <Card title="Premissas e ressalvas" sub="o que o agente assumiu para chegar aqui">
        {lista(a.premissas).length > 0 && (
          <ul className="mf-why">
            {lista(a.premissas).map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        )}
        <BlocoAuditoria
          limitacoes={lista(auditoria.limitacoes)}
          blocosRecebidos={lista(auditoria.blocos_recebidos)}
          blocosAusentes={lista(auditoria.blocos_ausentes)}
          idsCitados={lista(auditoria.ids_citados)}
          regrasVioladas={lista(auditoria.regras_de_piso_violadas)}
        >
          <Facts items={[
            { k: "teto anual", v: money(auditoria.teto_anual_brl) },
            { k: "base do teto", v: rotulo(BASE_TETO, auditoria.base_do_teto) },
            { k: "linhas no catálogo", v: auditoria.linhas_no_catalogo?.toString() ?? "—" },
            { k: "linhas proponíveis", v: auditoria.linhas_proponiveis?.toString() ?? "—" },
          ]} />
        </BlocoAuditoria>
      </Card>

      {/* 9 · o texto do agente */}
      <RelatorioEmTexto userId={user.user_id} agentId="agent_seguros" />

      {/* 10 · a procedência */}
      <Provenancia user={user} agentId="agent_seguros" />
    </div>
  );
}
